import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useTeamStore } from '@/stores/team'
import { useUIStore } from '@/stores/ui'
import { classNames, getDisplayName, daysUntil, type TrialStatus, type PlanKind } from '@/lib/utils'
import { openIntercomMessenger, shutdownIntercom } from '@/hooks/useIntercom'
import { useTrialStatus, usePlanKind, useIsInternalAdmin } from '@/hooks/useTrialStatus'
import { trackEvent, resetMixpanel } from '@/hooks/useMixpanel'
import { signOut } from '@/lib/firebase'
import { UserAvatar } from '@/components/UserAvatar'
import { Badge } from '@/components/Badge'
import { AccountAdminsModal } from '@/components/AccountAdminsModal'
import { API_HOST } from '@/lib/env'
import {
  IconChartBar,
  IconVideo,
  IconProgressCheck,
  IconCarouselHorizontal,
  IconCut,
  IconBrandYoutube,
  IconMovie,
  IconUsers,
  IconUser,
  IconDiscount,
  IconColorSwatch,
  IconPalette,
  IconNotification,
  IconMessageCircle,
  IconSelector,
  IconUserPlus,
  IconLogout,
  IconRefresh,
  IconBrandSpeedtest,
  IconMailForward,
  IconLayoutKanban,
  IconListDetails,
  IconTemplate,
  IconAdjustments,
  IconTargetArrow,
  IconClock,
} from '@/components/icons'

// Persistent warning strip when Settings > Support's env switcher has pointed this session at a
// non-prod backend — without it, nothing elsewhere in the app indicates you're off prod, so it's
// easy to switch for a support/debugging task, navigate away, and later misread staging/dev data
// as real. Prod itself renders nothing here, same as always.
function nonProdLabel(): string | null {
  if (API_HOST.includes('api-staging')) return 'STAGING'
  if (API_HOST.includes('api-dev')) return 'DEV'
  if (API_HOST.includes('api-prod')) return null
  return 'NON-PROD' // unrecognized host — still flag it rather than silently saying nothing
}

const TRIAL_DOT_COLOR: Record<TrialStatus, string> = {
  startTrial: 'bg-gray-400',
  trialActive: 'bg-emerald-500',
  trialEnding: 'bg-yellow-500',
  trialExpired: 'bg-red-500',
}

const PLAN_KIND_LABEL: Record<PlanKind, string> = {
  trial: 'Trial',
  subscription: 'Subscription',
}

function trialStatusText(status: TrialStatus, planExpiresAt: string | null): string {
  if (status === 'startTrial') return 'Get started'
  if (status === 'trialExpired') return 'Expired'
  if (!planExpiresAt) return ''
  const days = daysUntil(planExpiresAt)
  return `${days} day${days === 1 ? '' : 's'} left`
}

function TrialStatusBox({
  status,
  planKind,
  planExpiresAt,
}: {
  status: TrialStatus
  planKind: PlanKind
  planExpiresAt: string | null
}) {
  return (
    <div className="mx-3 mb-2 flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2">
      <span className={classNames('h-2 w-2 shrink-0 rounded-full', TRIAL_DOT_COLOR[status])} />
      <span className="text-xs font-semibold text-gray-900">{PLAN_KIND_LABEL[planKind]}</span>
      <span className="flex-1 truncate text-xs text-gray-500">{trialStatusText(status, planExpiresAt)}</span>
      <button
        onClick={openIntercomMessenger}
        className="shrink-0 text-xs font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-700"
      >
        Upgrade
      </button>
    </div>
  )
}

// Only shown to internal admins on a team whose trial/subscription has actually expired —
// otherwise there's nothing different to preview by switching. Kept deliberately low-key
// (outline only, no fill, faint text) since it's an internal-only affordance, not something
// that should compete for attention with the rest of the sidebar.
function InternalAdminPreviewToggle({ previewAsRegularAdmin }: { previewAsRegularAdmin: boolean }) {
  const togglePreviewAsRegularAdmin = useUIStore((s) => s.togglePreviewAsRegularAdmin)
  return (
    <div className="mb-1.5 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5">
      <span className="flex-1 truncate text-[11px] text-gray-400">
        {previewAsRegularAdmin ? 'Viewing as regular admin.' : 'Viewing as internal admin.'}
      </span>
      <button
        onClick={togglePreviewAsRegularAdmin}
        title="Switch"
        className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
      >
        <IconRefresh className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// Reports is intentionally left out of the nav for now (still routable at /reports, just not
// linked) — everything else keeps its previous behavior/conditionals, just regrouped.
const navSections = [
  {
    label: 'Launch Campaigns',
    items: [
      { to: '/campaigns', label: 'Campaigns', icon: IconVideo },
      { to: '/users', label: 'Users', icon: IconUser },
      { to: '/groups', label: 'Groups', icon: IconUsers, conditional: 'brandOnly' },
      { to: '/rewards', label: 'Rewards', icon: IconDiscount, conditional: 'brandOnly' },
      { to: '/branding', label: 'Branding', icon: IconColorSwatch, conditional: 'brandOnly' },
      { to: '/brand-kits', label: 'Brand Kits', icon: IconPalette, conditional: 'internalOnly' },
      { to: '/push', label: 'Push Notifications', icon: IconNotification, conditional: 'push' },
    ],
  },
  {
    label: 'Access Content',
    items: [
      { to: '/projects', label: 'Project Tracker', icon: IconProgressCheck },
      { to: '/clips', label: 'Clip Archive', icon: IconCarouselHorizontal, conditional: 'clipArchive' },
      { to: '/edited-content', label: 'Edited Content', icon: IconBrandYoutube },
      { to: '/edit-template', label: 'Rolla Editor Render', icon: IconMovie, conditional: 'internalOnly' },
      { to: '/orders', label: 'Rolla Editing Orders', icon: IconCut, conditional: 'brandOnly' },
    ],
  },
  {
    label: 'Outbound',
    items: [
      { to: '/outbound/review', label: 'Review Queue', icon: IconMailForward, conditional: 'internalOnly' },
      { to: '/outbound/pipeline', label: 'Pipeline', icon: IconLayoutKanban, conditional: 'internalOnly' },
      { to: '/outbound/leads', label: 'Lead Pool', icon: IconListDetails, conditional: 'internalOnly' },
      { to: '/outbound/templates', label: 'Templates', icon: IconTemplate, conditional: 'internalOnly' },
      { to: '/outbound/analytics', label: 'Analytics', icon: IconTargetArrow, conditional: 'internalOnly' },
      { to: '/outbound/settings', label: 'Settings', icon: IconAdjustments, conditional: 'internalOnly' },
      { to: '/outbound/audit-log', label: 'Audit Log', icon: IconClock, conditional: 'internalOnly' },
    ],
  },
  {
    label: 'Reports',
    items: [{ to: '/dashboard', label: 'Analytics', icon: IconChartBar }],
  },
  {
    label: 'Support',
    items: [
      { to: '/settings', label: 'Usage', icon: IconBrandSpeedtest },
      { action: 'messageUs' as const, label: 'Message Us', icon: IconMessageCircle },
      { action: 'inviteAdmins' as const, label: 'Invite Admins', icon: IconUserPlus },
    ],
  },
] as const

export function Sidebar() {
  const isGlobalAdmin = useAuthStore((s) => s.isGlobalAdmin)
  const profile = useAuthStore((s) => s.profile)
  const firebaseUser = useAuthStore((s) => s.firebaseUser)
  const teamAdmins = useAuthStore((s) => s.teamAdmins)
  const authReset = useAuthStore((s) => s.reset)
  const team = useTeamStore((s) => s.team)
  const hasPushAccess = useTeamStore((s) => s.hasPushAccess)
  const hasClipArchiveAccess = useTeamStore((s) => s.hasClipArchiveAccess)
  const isRealEstate = useTeamStore((s) => s.isRealEstate)
  const teamReset = useTeamStore((s) => s.reset)
  const openBrandSwitcher = useUIStore((s) => s.openBrandSwitcher)
  const navigate = useNavigate()
  const canSwitchTeams = teamAdmins.length > 1
  const trialStatus = useTrialStatus()
  const planKind = usePlanKind()
  const isInternalAdmin = useIsInternalAdmin()
  const previewAsRegularAdmin = useUIStore((s) => s.previewAsRegularAdmin)
  const [showAdmins, setShowAdmins] = useState(false)

  async function handleSignOut() {
    // Fired before resetMixpanel() so it's still attributed to the identified user's timeline.
    trackEvent('Log Out')
    shutdownIntercom()
    resetMixpanel()
    authReset()
    teamReset()
    await signOut()
    navigate('/login')
  }

  function handleActionClick(action: 'messageUs' | 'inviteAdmins') {
    if (action === 'messageUs') openIntercomMessenger()
    if (action === 'inviteAdmins') setShowAdmins(true)
  }

  function isVisible(item: unknown): boolean {
    const conditional = (item as { conditional?: string }).conditional
    if (conditional === 'push') return isRealEstate && hasPushAccess
    if (conditional === 'clipArchive') return hasClipArchiveAccess
    if (conditional === 'brandOnly') return !isRealEstate
    if (conditional === 'internalOnly') return isInternalAdmin
    return true
  }

  const filteredSections = navSections
    .map((section) => ({ ...section, items: section.items.filter(isVisible) }))
    .filter((section) => section.items.length > 0)

  const envLabel = nonProdLabel()

  return (
    <aside className="flex h-full w-64 flex-col border-r border-gray-200 bg-white">
      {envLabel && (
        <div className="bg-yellow-400 px-4 py-1 text-center text-xs font-bold tracking-wide text-yellow-950">
          {envLabel} ENVIRONMENT
        </div>
      )}
      {/* Team header — only switchable if the user admins more than one team */}
      {canSwitchTeams ? (
        <button
          onClick={() => openBrandSwitcher()}
          className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 hover:bg-gray-50 transition-colors"
        >
          {team?.logoURL ? (
            <img src={team.logoURL} alt={team.name} className="h-8 w-8 rounded-lg object-cover" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-sm font-bold text-white">
              {team?.name?.charAt(0) ?? 'R'}
            </div>
          )}
          <span className="flex-1 truncate text-left text-sm font-semibold text-gray-900">
            {team?.name ?? 'Select Team'}
          </span>
          <IconSelector className="h-4 w-4 text-gray-400" />
        </button>
      ) : (
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
          {team?.logoURL ? (
            <img src={team.logoURL} alt={team.name} className="h-8 w-8 rounded-lg object-cover" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-sm font-bold text-white">
              {team?.name?.charAt(0) ?? 'R'}
            </div>
          )}
          <span className="flex-1 truncate text-left text-sm font-semibold text-gray-900">
            {team?.name ?? 'Select Team'}
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-3">
        {filteredSections.map((section) => (
          <div key={section.label ?? 'ungrouped'}>
            {section.label && (
              <p className="mb-1 px-3 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                {section.label}
              </p>
            )}
            <ul className="space-y-1">
              {section.items.map((item) =>
                'to' in item ? (
                  <li key={item.label}>
                    <NavLink
                      to={item.to}
                      onClick={() => trackEvent('Nav Tab Clicked', { tab: item.label })}
                      className={({ isActive }) =>
                        classNames(
                          'flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-normal transition-colors',
                          isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {/* Brand teams still see Billing on this page; RE doesn't (Billing is
                          hidden for them), so the nav label reflects what's actually there. */}
                      {item.to === '/settings' ? (isRealEstate ? 'Usage' : 'Usage & Billing') : item.label}
                    </NavLink>
                  </li>
                ) : (
                  <li key={item.label}>
                    <button
                      type="button"
                      onClick={() => handleActionClick(item.action)}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-normal text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {item.label}
                    </button>
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
      </nav>

      {/* Subscription (standard license) teams only see this box once actually expired — a
          normal paid plan in good standing has nothing useful to show here. Trial teams keep
          the full status range (startTrial/active/ending/expired), since every stage is
          relevant while still deciding whether to subscribe. */}
      {trialStatus && planKind && (planKind === 'trial' || trialStatus === 'trialExpired') && (
        <TrialStatusBox status={trialStatus} planKind={planKind} planExpiresAt={team?.planExpiresAt ?? null} />
      )}

      {/* Footer */}
      <div className="border-t border-gray-200 px-4 py-2">
        {(profile || firebaseUser) && (
          <div className="mb-1.5 flex items-center gap-2.5">
            <UserAvatar
              profile={
                profile
                  ? {
                      firstName: profile.firstName,
                      lastName: profile.lastName,
                      photo: profile.avatarURL,
                      emailAddresses: profile.email ? [{ emailAddress: profile.email }] : [],
                    }
                  : null
              }
              size="sm"
            />
            <div className="min-w-0 flex-1">
              {(() => {
                const displayName = profile
                  ? getDisplayName({
                      ...profile,
                      emailAddresses: profile.email ? [{ emailAddress: profile.email }] : [],
                    })
                  : (firebaseUser?.email ?? 'Unknown user')
                const email = profile?.email ?? firebaseUser?.email
                // A brand-new profile with no firstName/lastName yet falls back to the email as
                // its display name — showing the same email again underneath is just noise.
                const showEmail = email && email !== displayName
                return (
                  <>
                    <p className="truncate text-xs font-medium text-gray-900">{displayName}</p>
                    {showEmail && <p className="truncate text-[11px] text-gray-400">{email}</p>}
                  </>
                )
              })()}
            </div>
            {isGlobalAdmin && (
              <Badge color="secondary" className="flex-shrink-0">
                Admin
              </Badge>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              title="Log Out"
              className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600"
            >
              <IconLogout className="h-4 w-4" />
            </button>
          </div>
        )}
        {isInternalAdmin && trialStatus === 'trialExpired' && (
          <InternalAdminPreviewToggle previewAsRegularAdmin={previewAsRegularAdmin} />
        )}
        <button
          onClick={() => navigate('/settings')}
          className="flex w-full justify-center py-1 opacity-70 hover:opacity-100 transition-opacity"
        >
          <img src="/rolla-logo.png" alt="Rolla" className="h-5 w-auto" />
        </button>
      </div>

      {showAdmins && <AccountAdminsModal onClose={() => setShowAdmins(false)} />}
    </aside>
  )
}
